import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useUser } from '../components/auth/AuthPage/UserContext';

const NotificationContext = createContext();

// Polling interval in milliseconds
const POLL_INTERVAL = 12000;

export function NotificationProvider({ children }) {
    const { user } = useUser();
    const [notificationCounts, setNotificationCounts] = useState({
        total: 0,
        byTrade: {},
        byTab: { incoming: 0, pending: 0, completed: 0 }
    });
    const [orphanedNotifications, setOrphanedNotifications] = useState([]);
    const [loading, setLoading] = useState(false);
    const [lastFetched, setLastFetched] = useState(null);

    // Fetch notification counts from the server
    const fetchNotificationCounts = useCallback(async () => {
        if (!user) {
            setNotificationCounts({ total: 0, byTrade: {}, byTab: { incoming: 0, pending: 0, completed: 0 } });
            setOrphanedNotifications([]);
            return;
        }

        try {
            setLoading(true);
            
            // Fetch both regular counts and orphaned notifications in parallel
            const [countsResponse, orphanedResponse] = await Promise.all([
                fetch('/api/notifications/unread-count', { credentials: 'include' }),
                fetch('/api/notifications/orphaned', { credentials: 'include' })
            ]);

            if (countsResponse.ok) {
                const data = await countsResponse.json();
                setNotificationCounts({
                    total: data.total || 0,
                    byTrade: data.byTrade || {},
                    byTab: data.byTab || { incoming: 0, pending: 0, completed: 0 }
                });
            }

            if (orphanedResponse.ok) {
                const data = await orphanedResponse.json();
                setOrphanedNotifications(data.notifications || []);
            }

            setLastFetched(new Date());
        } catch (error) {
            console.error('Error fetching notification counts:', error);
        } finally {
            setLoading(false);
        }
    }, [user]);

    // Fetch immediately when user logs in
    useEffect(() => {
        if (user) {
            fetchNotificationCounts();
        } else {
            setNotificationCounts({ total: 0, byTrade: {}, byTab: { incoming: 0, pending: 0, completed: 0 } });
        }
    }, [user, fetchNotificationCounts]);

    // Set up polling interval + instant refresh when the tab regains focus
    useEffect(() => {
        if (!user) return;

        const intervalId = setInterval(() => {
            if (!document.hidden) fetchNotificationCounts();
        }, POLL_INTERVAL);

        const onFocus = () => fetchNotificationCounts();
        window.addEventListener('focus', onFocus);

        return () => {
            clearInterval(intervalId);
            window.removeEventListener('focus', onFocus);
        };
    }, [user, fetchNotificationCounts]);

    // Mark notifications for a specific trade as read
    const markTradeAsRead = useCallback(async (tradeId) => {
        if (!user || !tradeId) return;

        try {
            const response = await fetch(`/api/notifications/trade/${tradeId}/read`, {
                method: 'PATCH',
                credentials: 'include'
            });

            if (response.ok) {
                // Optimistic local update (keep byTab intact!), then resync
                // with the server so tab badges settle in one click
                setNotificationCounts(prev => {
                    const tradeCount = prev.byTrade[tradeId] || 0;
                    const newByTrade = { ...prev.byTrade };
                    delete newByTrade[tradeId];
                    return {
                        ...prev,
                        total: Math.max(0, prev.total - tradeCount),
                        byTrade: newByTrade
                    };
                });
                fetchNotificationCounts();
            }
        } catch (error) {
            console.error('Error marking trade notifications as read:', error);
        }
    }, [user]);

    // Mark all notifications as read
    const markAllAsRead = useCallback(async () => {
        if (!user) return;

        try {
            const response = await fetch('/api/notifications/read-all', {
                method: 'PATCH',
                credentials: 'include'
            });

            if (response.ok) {
                setNotificationCounts({ total: 0, byTrade: {}, byTab: { incoming: 0, pending: 0, completed: 0 } });
                setOrphanedNotifications([]);
            }
        } catch (error) {
            console.error('Error marking all notifications as read:', error);
        }
    }, [user]);

    // Dismiss a single orphaned notification (mark as read)
    const dismissNotification = useCallback(async (notificationId) => {
        if (!user || !notificationId) return;

        try {
            const response = await fetch(`/api/notifications/${notificationId}/read`, {
                method: 'PATCH',
                credentials: 'include'
            });

            if (response.ok) {
                // Remove from local state immediately
                setOrphanedNotifications(prev => 
                    prev.filter(n => n.id !== notificationId)
                );
                // Also decrement total count
                setNotificationCounts(prev => ({
                    ...prev,
                    total: Math.max(0, prev.total - 1)
                }));
            }
        } catch (error) {
            console.error('Error dismissing notification:', error);
        }
    }, [user]);

    // Force refresh notifications (useful after actions)
    const refreshNotifications = useCallback(() => {
        fetchNotificationCounts();
    }, [fetchNotificationCounts]);

    // Check if a specific trade has notifications
    const hasTradeNotification = useCallback((tradeId) => {
        return (notificationCounts.byTrade[tradeId] || 0) > 0;
    }, [notificationCounts.byTrade]);

    // Get notification count for a specific trade
    const getTradeNotificationCount = useCallback((tradeId) => {
        return notificationCounts.byTrade[tradeId] || 0;
    }, [notificationCounts.byTrade]);

    return (
        <NotificationContext.Provider value={{
            total: notificationCounts.total,
            byTrade: notificationCounts.byTrade,
            byTab: notificationCounts.byTab,
            orphanedNotifications,
            loading,
            lastFetched,
            markTradeAsRead,
            markAllAsRead,
            dismissNotification,
            refreshNotifications,
            hasTradeNotification,
            getTradeNotificationCount
        }}>
            {children}
        </NotificationContext.Provider>
    );
}

export function useNotifications() {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotifications must be used within a NotificationProvider');
    }
    return context;
}

